import type { fabric } from 'fabric';
import React, { useCallback, useMemo } from 'react';
import {
  PLACEHOLDER_KEY_REGISTRY,
  getKeysForKind,
  isKeyCompatibleWithKind,
  missingFieldsFor,
} from 'modules/manager/domain/placeholder/placeholder-key-registry';
import { isKnownPlaceholderKey } from 'modules/manager/domain/placeholder/placeholder-keys';
import type { PlaceholderData, PlaceholderKind } from 'modules/manager/domain/placeholder/types';
import type { ObjectInspectorProps } from '../types';
import CommitOptionalNumberField from './commit-optional-number-field';
import CommitTextField from './commit-text-field';
import InspectorRow from './inspector-row';
import InspectorSection from './inspector-section';
import SelectField from './select-field';
import { useFabricData } from './use-fabric-data';

const UNSET_OPTION = { value: '', label: 'Unset' };

const PlaceholderIdentitySection: React.FC<ObjectInspectorProps> = ({ object, editor }) => {
  const [key, setKey] = useFabricData<string>(object, 'placeholderKey', editor);
  const [label, setLabel] = useFabricData<string>(object, 'placeholderLabel', editor);
  const [teamNumber, setTeamNumber] = useFabricData<number>(object, 'teamNumber', editor);
  const [playerNumber, setPlayerNumber] = useFabricData<number>(object, 'playerNumber', editor);

  const kind = (object.data?.kind ?? undefined) as PlaceholderKind | undefined;

  const refit = useCallback(() => {
    editor.placeholders?.refit(object as fabric.Group);
  }, [editor, object]);

  const keyOptions = useMemo(() => {
    const base = kind
      ? getKeysForKind(kind).map((value) => ({
          value,
          label: `${PLACEHOLDER_KEY_REGISTRY[value].label} (${value})`,
        }))
      : [];
    const opts = [UNSET_OPTION, ...base];
    if (key && !isKnownPlaceholderKey(key)) {
      return [{ value: key, label: `(unknown: ${key})`, disabled: true }, ...opts];
    }
    if (key && !isKeyCompatibleWithKind(key, kind)) {
      return [{ value: key, label: `(wrong type: ${key})`, disabled: true }, ...opts];
    }
    return opts;
  }, [key, kind]);

  const missing = useMemo(() => {
    const data = (object.data ?? {}) as Partial<PlaceholderData> & { placeholderKey?: string };
    return new Set(
      missingFieldsFor({
        ...data,
        placeholderKey: key ?? '',
        teamNumber,
        playerNumber,
      } as PlaceholderData),
    );
  }, [object, key, teamNumber, playerNumber]);

  return (
    <InspectorSection title="Identity">
      <InspectorRow label="Key">
        <SelectField
          value={key ?? ''}
          onChange={(v) => {
            setKey(v);
            refit();
          }}
          options={keyOptions}
        />
      </InspectorRow>
      <InspectorRow label="Label">
        <CommitTextField
          value={label}
          onCommit={(v) => {
            setLabel(v);
            refit();
          }}
          placeholder="Human label"
        />
      </InspectorRow>
      <InspectorRow label="Team #" highlight={missing.has('teamNumber')}>
        <CommitOptionalNumberField
          value={teamNumber}
          onCommit={(v) => {
            setTeamNumber(v);
            refit();
          }}
          min={1}
        />
      </InspectorRow>
      <InspectorRow label="Player #" highlight={missing.has('playerNumber')}>
        <CommitOptionalNumberField
          value={playerNumber}
          onCommit={(v) => {
            setPlayerNumber(v);
            refit();
          }}
          min={1}
        />
      </InspectorRow>
    </InspectorSection>
  );
};

export default PlaceholderIdentitySection;
